import CTABanner from '@/components/shared/CTABanner';
import SEO from '@/components/shared/SEO';
import { heroImages } from '@/data/images';
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from '@/components/ui/carousel';
import { Target, Eye, Heart, Handshake, TrendingUp, Scale } from 'lucide-react';

const values = [
  { icon: Target, title: 'Precision', desc: 'Every filing is meticulously prepared and reviewed to ensure the highest standard of accuracy and compliance.' },
  { icon: Eye, title: 'Transparency', desc: 'Clear communication, upfront pricing, and regular updates keep you informed throughout the entire process.' },
  { icon: Heart, title: 'Dedication', desc: 'Your brand protection is personal to us. We treat every case with the care and attention it deserves.' },
  { icon: Handshake, title: 'Integrity', desc: 'We operate with unwavering ethical standards, always putting your interests first in every decision.' },
  { icon: TrendingUp, title: 'Excellence', desc: 'Continuous improvement drives our team to deliver exceptional results and industry-leading success rates.' },
  { icon: Scale, title: 'Advocacy', desc: 'We are fierce advocates for your intellectual property rights, standing beside you when it matters most.' },
];

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      <SEO
        title="About Us - Our Mission, Values & Approach"
        description="Learn about Trademark Pact's mission to make professional trademark protection accessible to every business. Trusted by 10,000+ brands nationwide."
      />
      {/* Hero */}
      <section className="relative pt-28 pb-12 lg:pt-36 lg:pb-24 overflow-hidden" data-testid="about-hero">
        <div className="absolute inset-0 z-0">
          <img src={heroImages.about} alt="" className="w-full h-full object-cover" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#064E3B]/95 via-[#064E3B]/80 to-[#064E3B]/50" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <span className="text-emerald-300 font-semibold text-sm tracking-wider uppercase">About Us</span>
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white leading-tight tracking-tight mt-3 mb-6">
              We Build Legal Shields Around the Brands You Love
            </h1>
            <p className="text-emerald-100 text-base lg:text-lg leading-relaxed">
              Trademark Pact is a dedicated team of trademark professionals, legal experts, and brand strategists committed to helping businesses protect and grow their most valuable assets — their brands.
            </p>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-12 lg:py-28 bg-white" data-testid="about-mission">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#10B981] font-semibold text-sm tracking-wider uppercase">Our Mission</span>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mt-3 mb-6">
                Making Trademark Protection Accessible to Every Business
              </h2>
              <p className="text-slate-600 leading-relaxed mb-5">
                We believe that every business, regardless of size, deserves world-class trademark protection. Our mission is to simplify the complex world of intellectual property law and make professional brand protection accessible to entrepreneurs, startups, and established businesses alike.
              </p>
              <p className="text-slate-600 leading-relaxed">
                Through a combination of legal expertise, technology-driven efficiency, and personalized service, we have helped thousands of businesses secure and maintain their trademark rights. Our commitment goes beyond filing applications — we are your long-term partners in brand protection.
              </p>
            </div>
            <div>
              <img
                src="https://images.pexels.com/photos/7108454/pexels-photo-7108454.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Professional team collaboration"
                className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="about-values">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-[#064E3B] mb-4">
              Our Core Values
            </h2>
            <p className="text-slate-600 text-base lg:text-lg max-w-2xl mx-auto">
              These principles guide everything we do and shape the way we serve our clients.
            </p>
          </div>
          <div className="relative px-8">
            <Carousel opts={{ align: 'start', loop: true }}>
              <CarouselContent className="-ml-4">
                {values.map((v, i) => {
                  const Icon = v.icon;
                  return (
                    <CarouselItem key={i} className="pl-4 basis-[100%] md:basis-[50%] lg:basis-[33.333%]">
                      <div className="bg-white p-7 rounded-2xl border border-emerald-100 card-hover" data-testid={`about-value-${i}`}>
                        <Icon className="w-8 h-8 text-[#10B981] mb-4" />
                        <h3 className="font-heading font-bold text-base text-[#064E3B] mb-2">{v.title}</h3>
                        <p className="text-sm text-slate-600 leading-relaxed">{v.desc}</p>
                      </div>
                    </CarouselItem>
                  );
                })}
              </CarouselContent>
              <CarouselPrevious className="border-emerald-200 text-[#064E3B]" />
              <CarouselNext className="border-emerald-200 text-[#064E3B]" />
            </Carousel>
          </div>
        </div>
      </section>

      {/* Approach */}
      <section className="py-12 lg:py-28 bg-white" data-testid="about-approach">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mb-6">
              Our Approach
            </h2>
            <p className="text-slate-600 leading-relaxed mb-5">
              We take a comprehensive, client-centered approach to brand protection. Every engagement begins with understanding your unique business goals and brand identity. From there, we develop customized strategies that align your intellectual property protection with your business objectives.
            </p>
            <p className="text-slate-600 leading-relaxed mb-5">
              Our team combines deep legal expertise with practical business insight. We do not just file paperwork — we provide strategic counsel that helps you make informed decisions about your brand's future. Whether you are registering your first trademark or managing a complex portfolio, our approach remains the same: thorough, proactive, and personally invested in your success.
            </p>
            <p className="text-slate-600 leading-relaxed">
              We are proud of the relationships we build with our clients. Many of the businesses we serve today have been with us since their first filing. That lasting trust is the greatest testament to the quality of our work and the dedication of our team.
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-[#064E3B]" data-testid="about-stats">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { num: '10,000+', label: 'Trademarks Filed' },
              { num: '98%', label: 'Success Rate' },
              { num: '50+', label: 'States Covered' },
              { num: '24/7', label: 'Support Available' },
            ].map((stat, i) => (
              <div key={i} data-testid={`about-stat-${i}`}>
                <div className="font-heading font-extrabold text-3xl lg:text-4xl text-white">{stat.num}</div>
                <div className="text-emerald-300 text-sm mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTABanner />
    </div>
  );
}
